import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Sample, sampleService } from '../../services/sample.service';
import { storageRoomService } from '../../services/storageRoom.service';
import { isSampleLocalMode } from '../../services/systems/sample-local/sampleLocalMode';
import { useAuth } from '../../hooks/useAuth';
import { formatUserName } from '../../utils/name.util';
import { translateAxiosErrorMessage } from '../../utils/error.utils';
import { useSampleFormModalHydration } from '../../hooks/useSampleFormModalHydration';
import { useSampleFormUsageRecords } from '../../hooks/useSampleFormUsageRecords';
import ConfirmDestructiveModal from '../common/ConfirmDestructiveModal';
import SampleFormBasicFields from './SampleFormBasicFields';
import { AppModalCloseButton } from '../common/AppModalCloseButton';
import SampleFormOptionalSections from './SampleFormOptionalSections';
import SampleFormStorageFields from './SampleFormStorageFields';
import { filterStorageRoomsForSelect } from '../../utils/storageRoomSearch';
import SampleFormDrawerFields from './SampleFormDrawerFields';
import SampleFormUsageHistorySection from './SampleFormUsageHistorySection';
import { buildUsageRecordsSignature } from '../../utils/sampleUsageRecordSignature';
import { findUsageDateBeforeCreated } from '../../utils/sampleFormUsageRecordEdit';
import { buildSampleCreateFormData } from '../../utils/sampleFormInit';
import { buildSampleFormSchema, type SampleFormData } from '../../utils/sampleFormSchema';
import { SAMPLE_DEFAULT_UNIT } from '../../utils/sampleUnitLabels';
import { loadLastSampleInputData, saveLastSampleInputData } from '../../utils/sampleFormLastInput';
import { buildSampleSubmitPayload } from '../../utils/sampleFormSubmitPayload';
import {
  focusSampleFormField,
  isSampleFormInfoError,
} from '../../utils/sampleFormErrorAlert';
import { resolveSampleNameForSubmit } from '../../utils/softBreakIdentifier';
import './sampleForm.css';
import './sampleIcons.css';

interface SampleFormModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: Partial<Sample>) => Promise<void>;
  initialData?: Sample | Partial<Sample> | null;
  isLoading?: boolean;
  /** 管理者の「全保管庫表示」トグル（未指定時は自分＋共有のみ） */
  adminStorageViewAll?: boolean;
}

export default function SampleFormModal({
  show,
  onHide,
  onSubmit,
  initialData,
  isLoading = false,
  adminStorageViewAll = false,
}: SampleFormModalProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  // initialData に id がある場合のみ編集モード
  const isEditMode = !!initialData?.id;
  // 編集時のみ actual を使う。新規の initialData は位置プリフィル
  const createLocationPrefill = !isEditMode && initialData ? initialData : undefined;

  // マウント時（親が show 時だけ描画する想定）に前回入力を同期初期化し、空フォームのチラつきを避ける
  const createDefaultValues = useMemo(
    () =>
      isEditMode
        ? null
        : buildSampleCreateFormData(loadLastSampleInputData(), {
            createLocationPrefill: createLocationPrefill ?? undefined,
          }),
    // モーダル open ごとのマウントで一度だけ
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once defaults
    []
  );

  const [error, setError] = useState<string>('');
  const [pathogenOption, setPathogenOption] = useState<'included' | 'excluded'>(() =>
    createDefaultValues?.pathogenOption === 'included' ? 'included' : 'excluded'
  );
  const [storageSearch, setStorageSearch] = useState<string>('');
  const [selectedStorageRoomId, setSelectedStorageRoomId] = useState<number | undefined>(
    () => createDefaultValues?.storageRoomId
  );
  const [sampleCreatedDate, setSampleCreatedDate] = useState<string>('');
  /** 使用履歴行削除の確認 */
  const [usageRecordDeleteIndex, setUsageRecordDeleteIndex] = useState<number | null>(null);
  const [notesFoldOpen, setNotesFoldOpen] = useState(false);
  const [pathogenFoldOpen, setPathogenFoldOpen] = useState(false);
  const initialUsageRecordsSignatureRef = useRef('');
  const isSubmittingRef = useRef(false);
  /** 編集モーダルでフォーム初期化済みのサンプル ID（React Query 再取得による reset 防止） */
  const formInitializedSampleIdRef = useRef<number | null>(null);

  const creatorDisplayName = user
    ? formatUserName(user.firstName, user.lastName, i18n.language) ||
      user.email ||
      user.username ||
      ''
    : isSampleLocalMode()
      ? t('sample.zip_local_creator', { defaultValue: 'ローカル' })
      : '';

  // initialDataがある場合、最新のサンプルデータを取得（使用履歴が更新されるため）
  const { data: latestSampleData } = useQuery({
    queryKey: ['samples', 'detail', initialData?.id, adminStorageViewAll],
    queryFn: () =>
      sampleService.getById(initialData!.id, {
        adminViewAll: adminStorageViewAll || undefined,
      }),
    enabled: !!initialData?.id && show,
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const actualInitialData = latestSampleData?.data || (isEditMode ? initialData : undefined);

  // 保管庫一覧（FilterPanel と同一キーでキャッシュ共有。認証前は走らせない）
  const {
    data: storageRoomsData,
    error: storageRoomsError,
    isPending: storageRoomsPending,
    refetch: refetchStorageRooms,
  } = useQuery({
    queryKey: ['storage-rooms', { limit: 1000, adminViewAll: adminStorageViewAll }],
    queryFn: () =>
      storageRoomService.list({
        limit: 1000,
        adminViewAll: adminStorageViewAll || undefined,
      }),
    enabled: show && !!user,
    staleTime: 60_000,
    retry: 1,
    refetchOnMount: 'always',
  });

  const storageRooms = storageRoomsData?.data || [];

  const filteredStorageRooms = useMemo(
    () =>
      filterStorageRoomsForSelect(
        storageRooms,
        storageSearch,
        selectedStorageRoomId,
        i18n.language
      ),
    [storageRooms, storageSearch, selectedStorageRoomId, i18n.language]
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<SampleFormData>({
    resolver: zodResolver(buildSampleFormSchema(t, isEditMode)),
    defaultValues: createDefaultValues ?? {
      name: '',
      sampleType: '',
      bslLevel: '',
      initialQuantity: null,
      remainingQuantity: null,
      unit: SAMPLE_DEFAULT_UNIT,
      storageDate: '',
      disposalDate: '',
      notes: '',
      storageRoomId: undefined,
      drawerNumber: null,
      caseName: '',
      pathogenOption: 'excluded',
      pathogenGenus: '',
      pathogenSpecies: '',
      pathogenSubspecies: '',
      pathogenStrain: '',
      pathogenNote: '',
      drawerNote: '',
    },
  });

  const {
    usageRecords,
    setUsageRecords,
    calculatedRemainingQuantity,
    addUsageRecord,
    removeUsageRecord,
    updateUsageRecord,
  } = useSampleFormUsageRecords({
    t,
    sampleCreatedDate,
    initialQuantityFallback: actualInitialData?.initialQuantity,
    remainingQuantityFallback: actualInitialData?.remainingQuantity,
    recordedBy: creatorDisplayName,
    setError,
    setInitialQuantity: (n) => setValue('initialQuantity', n),
  });

  useSampleFormModalHydration({
    show,
    isEditMode,
    initialData,
    createLocationPrefill,
    storageRooms,
    language: i18n.language,
    t,
    reset,
    setValue,
    setPathogenOption,
    setSampleCreatedDate,
    setUsageRecords,
    setSelectedStorageRoomId,
    selectedStorageRoomId,
    setStorageSearch,
    setError,
    formInitializedSampleIdRef,
    initialUsageRecordsSignatureRef,
  });

  // フォームの値を監視
  const formValues = watch();
  const pathogenPreview = [
    formValues.pathogenGenus,
    formValues.pathogenSpecies,
    formValues.pathogenStrain,
  ]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)
    .join(' ');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 新規は折りたたみ閉じ、編集は値があるときだけ開く
  useEffect(() => {
    if (!show) return;
    if (!isEditMode) {
      setNotesFoldOpen(false);
      setPathogenFoldOpen(false);
      return;
    }
    const src = actualInitialData;
    if (!src) return;
    setNotesFoldOpen(Boolean(String(src.notes ?? '').trim()));
    setPathogenFoldOpen(
      Boolean(
        src.pathogenGenus ||
          src.pathogenSpecies ||
          src.pathogenSubspecies ||
          src.pathogenStrain ||
          src.pathogenNote
      )
    );
  }, [show, isEditMode, actualInitialData?.id]);

  // フォームの値が変更されたらlocalStorageに保存（debounce）
  useEffect(() => {
    if (actualInitialData) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveLastSampleInputData({
        ...formValues,
        pathogenOption,
        sampleCreatedDate,
      });
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formValues, pathogenOption, sampleCreatedDate, actualInitialData]);

  const onSubmitForm = async (data: SampleFormData) => {
    if (isSubmittingRef.current || isLoading) {
      return;
    }

    try {
      isSubmittingRef.current = true;
      setError('');

      if (
        actualInitialData &&
        findUsageDateBeforeCreated(usageRecords, sampleCreatedDate)
      ) {
        setError(t('sample.usage_date_before_created_variant2'));
        return;
      }

      const dataForSubmit: SampleFormData = {
        ...data,
        name: resolveSampleNameForSubmit(data.name, actualInitialData?.name, t),
      };
      const { formData, usageRecordsChanged } = buildSampleSubmitPayload({
        data: dataForSubmit,
        actualInitialData,
        usageRecords,
        pathogenOption,
        sampleCreatedDate,
        calculatedRemainingQuantity,
        initialUsageRecordsSignature: initialUsageRecordsSignatureRef.current,
      });
      await onSubmit(formData);

      if (usageRecordsChanged) {
        initialUsageRecordsSignatureRef.current = buildUsageRecordsSignature(usageRecords);
      }

      // 新規作成時のみ、保存成功後にlocalStorageに保存（次回も使えるように）
      if (!actualInitialData) {
        saveLastSampleInputData({
          ...data,
          pathogenOption,
          sampleCreatedDate,
        });
      }

      onHide();
    } catch (err: unknown) {
      setError(translateAxiosErrorMessage(err, t, t('common.save_failed')));
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const onValidationError = (fieldErrors: FieldErrors<SampleFormData>) => {
    const firstKey = Object.keys(fieldErrors)[0] as keyof SampleFormData | undefined;
    const firstError = firstKey ? fieldErrors[firstKey] : undefined;
    const message =
      typeof firstError?.message === 'string'
        ? firstError.message
        : t('common.validation_error', {
            defaultValue: t('common.validation_failed', {
              defaultValue: '入力内容を確認してください。',
            }),
          });
    setError(message);
    // 長いモーダルでエラーが埋もれないよう、該当欄へスクロール
    window.requestAnimationFrame(() => focusSampleFormField(firstKey));
  };

  useEffect(() => {
    if (!error) return;
    const banner = document.getElementById('sample-form-error');
    banner?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  const drawerNumberRegister = register('drawerNumber', {
    setValueAs: (value) => {
      if (value === '' || value === null || value === undefined) return null;
      const n = typeof value === 'number' ? value : parseInt(String(value), 10);
      return Number.isNaN(n) ? null : n;
    },
  });

  const errorIsInfo = error ? isSampleFormInfoError(error, t('sample.duplicate_name_created_date')) : false;

  return (
    <>
    <Modal
      dialogClassName="sample-system-dialog"
      show={show}
      onHide={onHide}
      size="xl"
      scrollable
      enforceFocus={false}
    >
      <Modal.Header>
        <Modal.Title className="sample-detail-modal-title pe-2">
          <i
            className={`fas ${isEditMode ? 'fa-edit' : 'fa-plus'} me-2 text-primary`}
            aria-hidden="true"
          />
          {isEditMode ? t('sample.modal_edit_title') : t('sample.modal_create_title')}
        </Modal.Title>
        <AppModalCloseButton
          onClick={onHide}
          ariaLabel={t('common.close')}
          title={
            isEditMode
              ? t('sample.modal_edit_close_title', { defaultValue: t('common.close') })
              : t('sample.modal_create_close_title', { defaultValue: t('common.close') })
          }
        />
      </Modal.Header>
      {/*
        Form で Body+Footer を包むと Bootstrap scrollable の flex が壊れ、
        長い編集フォームでフッタが見切れる。Body 内だけ Form、送信は form= 属性。
      */}
      <Modal.Body className="p-3 p-md-4">
        <Form
          id="sample-form"
          onSubmit={handleSubmit(onSubmitForm, onValidationError)}
          noValidate
        >
          {error ? (
            <Alert
              id="sample-form-error"
              variant={errorIsInfo ? 'info' : 'danger'}
              className="mb-3 sticky-top"
              style={{ zIndex: 2 }}
              dismissible
              onClose={() => setError('')}
            >
              <Alert.Heading className="h6 mb-1">
                <i
                  className={`fas ${errorIsInfo ? 'fa-info-circle' : 'fa-exclamation-triangle'} me-2`}
                ></i>
                {errorIsInfo ? t('common.info') : t('common.error')}
              </Alert.Heading>
              {error}
            </Alert>
          ) : null}

          <SampleFormBasicFields
            register={register}
            errors={errors}
            isEditMode={isEditMode}
            creatorDisplayName={creatorDisplayName}
            sampleCreatedDate={sampleCreatedDate}
            onSampleCreatedDateChange={setSampleCreatedDate}
            sampleTypeValue={formValues.sampleType || ''}
          />

          <SampleFormStorageFields
            storageSearch={storageSearch}
            onStorageSearchChange={setStorageSearch}
            onClearSearch={() => {
              setStorageSearch('');
              setSelectedStorageRoomId(undefined);
              setValue('storageRoomId', undefined);
            }}
            storageRoomsError={!!storageRoomsError}
            storageRoomsPending={storageRoomsPending}
            onRetryStorageRooms={() => {
              void refetchStorageRooms();
            }}
            filteredStorageRooms={filteredStorageRooms}
            allStorageRooms={storageRooms}
            totalStorageRoomCount={storageRooms.length}
            selectedStorageRoomId={selectedStorageRoomId}
            onStorageRoomChange={(roomId) => {
              setSelectedStorageRoomId(roomId);
              setValue('storageRoomId', roomId);
            }}
            isInvalid={!!errors.storageRoomId}
            invalidMessage={errors.storageRoomId?.message}
          />

          <SampleFormDrawerFields
            register={register}
            drawerNumberRegister={drawerNumberRegister}
          />

          <SampleFormOptionalSections
            control={control}
            register={register}
            setValue={setValue}
            notesFoldOpen={notesFoldOpen}
            onNotesFoldOpenChange={setNotesFoldOpen}
            pathogenFoldOpen={pathogenFoldOpen}
            onPathogenFoldOpenChange={setPathogenFoldOpen}
            pathogenOption={pathogenOption}
            onPathogenOptionChange={setPathogenOption}
            pathogenPreview={pathogenPreview}
          />

          {isEditMode ? (
            <SampleFormUsageHistorySection
              records={usageRecords}
              sampleCreatedDate={sampleCreatedDate}
              language={i18n.language}
              onAdd={addUsageRecord}
              onFieldChange={updateUsageRecord}
              onRequestDelete={setUsageRecordDeleteIndex}
            />
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={isLoading}
          aria-label={t('common.cancel')}
          title={t('common.cancel')}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="sample-form"
          disabled={isLoading}
          aria-label={
            isLoading
              ? t('common.saving')
              : isEditMode
                ? t('common.update')
                : t('common.create')
          }
          title={
            isLoading
              ? t('common.saving')
              : isEditMode
                ? t('sample.submit_update_title', {
                    defaultValue: 'サンプルを更新する',
                  })
                : t('sample.submit_create_title', {
                    defaultValue: 'サンプルを作成する',
                  })
          }
        >
          {isLoading
            ? t('common.saving')
            : isEditMode
              ? t('common.update')
              : t('common.create')}
        </Button>
      </Modal.Footer>
    </Modal>
    <ConfirmDestructiveModal
      show={usageRecordDeleteIndex !== null}
      onHide={() => setUsageRecordDeleteIndex(null)}
      message={t('sample.remove_usage_record_confirm')}
      onConfirm={() => {
        if (usageRecordDeleteIndex !== null) {
          removeUsageRecord(usageRecordDeleteIndex);
        }
      }}
    />
    </>
  );
}
